import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";
import { Search, ExternalLink, MapPin, Building2, DollarSign, Wand2 } from "lucide-react";

const COUNTRIES = [
  { code: "us", label: "United States" },
  { code: "gb", label: "United Kingdom" },
  { code: "ca", label: "Canada" },
  { code: "au", label: "Australia" },
  { code: "de", label: "Germany" },
  { code: "fr", label: "France" },
  { code: "in", label: "India" },
];

export default function JobSearch() {
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [country, setCountry] = useState("us");
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [salaryMin, setSalaryMin] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const resultsRef = useRef(null);

  // Remembered salary preference — prefill on arrival.
  useEffect(() => {
    api.get("/job-prefs")
      .then((r) => { if (r.data?.salary_min) setSalaryMin(String(r.data.salary_min)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (data) resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [data]);

  const tailorThisJob = (job) => {
    navigate("/feed-job", {
      state: {
        prefill: {
          jobTitle: job.title || "",
          companyName: job.company || "",
          jobDescription: job.description || "",
          source: "adzuna",
          adzuna_url: job.url || "",
        },
      },
    });
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    setError("");
    if (!role.trim()) {
      setError("Tell Resmo what kind of role to hunt.");
      return;
    }
    setLoading(true);
    // Remember the salary floor for future hunts + AI matches.
    api.patch("/job-prefs", { salary_min: salaryMin ? parseInt(salaryMin, 10) : 0 }).catch(() => {});
    try {
      const { data: res } = await api.post("/jobs-search", {
        role,
        location: remoteOnly ? "" : location,
        country,
        remote: remoteOnly,
        salary_min: salaryMin ? parseInt(salaryMin, 10) : null,
      });
      setData(res);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="rm-tag mb-3">Hunt mode</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase">
              What's Resmo <span className="rm-text-orange">hungry for?</span>
            </h1>
            <p className="text-base md:text-lg mt-2 max-w-2xl" style={{ color: "#475569" }}>
              Drop a role title and a location. Resmo sniffs out live listings and serves up
              curated search links across every major job board.
            </p>
          </div>
          <ResmoMascot state="hungry" size={140} />
        </div>

        <form onSubmit={handleSearch} className="rm-card-static p-6 md:p-8 mb-8" data-testid="job-search-form">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="rm-label block mb-2">Role</label>
              <input
                className="rm-input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Senior Product Designer"
                data-testid="job-search-role"
              />
            </div>
            <div className="md:col-span-3">
              <label className="rm-label block mb-2">Location</label>
              <input
                className="rm-input"
                value={remoteOnly ? "" : location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={remoteOnly ? "Remote — anywhere" : "NYC · Denver · London"}
                disabled={remoteOnly}
                style={remoteOnly ? { opacity: 0.5 } : undefined}
                data-testid="job-search-location"
              />
              <label className="mt-2 flex items-center gap-2 cursor-pointer select-none"
                     data-testid="job-search-remote-label">
                <input
                  type="checkbox"
                  checked={remoteOnly}
                  onChange={(e) => setRemoteOnly(e.target.checked)}
                  className="w-4 h-4 accent-[#FF6A00] cursor-pointer"
                  data-testid="job-search-remote-toggle"
                />
                <span className="font-mono-rm text-xs font-bold uppercase tracking-wider"
                      style={{ color: "#0F1B2D" }}>
                  Remote only
                </span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="rm-label block mb-2">Country</label>
              <select
                className="rm-input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                data-testid="job-search-country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="rm-label block mb-2">Min salary</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-rm text-sm"
                      style={{ color: "#475569" }}>$</span>
                <input
                  className="rm-input pl-7"
                  type="number"
                  min="0"
                  step="5000"
                  value={salaryMin}
                  onChange={(e) => setSalaryMin(e.target.value)}
                  placeholder="90,000"
                  data-testid="job-search-salary-min"
                />
              </div>
              <div className="mt-1 font-mono-rm text-[10px]" style={{ color: "#64748B" }}>
                Only show jobs paying at least this — leave blank for all
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button type="submit" className="rm-btn rm-btn-primary" disabled={loading} data-testid="job-search-submit">
              <Search size={16} /> {loading ? "Sniffing..." : "Hunt jobs"}
            </button>
            {loading && (
              <ProgressMeter compact expectedSeconds={6} label="Hunting" testid="job-search-progress-meter" />
            )}
            {data && data.adzuna_enabled === false && (
              <span className="rm-tag" data-testid="adzuna-disabled-tag">
                Live listings off — add Adzuna keys to enable
              </span>
            )}
          </div>
          {error && (
            <div className="mt-4 p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="job-search-error">
              {error}
            </div>
          )}
        </form>

        {data && (
          <div className="space-y-8" ref={resultsRef}>
            {data.adzuna_enabled !== false && (data.listings || []).length === 0 && (
              <div className="rm-card-static p-5 font-mono-rm text-sm"
                   style={{ background: "#FFF7E6" }}
                   data-testid="job-search-no-listings">
                No live listings came back for that exact search — try a broader
                title (e.g. &ldquo;Project Manager&rdquo;) or a different location.
                The curated boards below have you covered meanwhile.
              </div>
            )}

            {(data.listings || []).length > 0 && (
              <section data-testid="job-search-listings">
                <h2 className="font-display text-2xl uppercase mb-4">
                  Live listings <span className="font-mono-rm text-sm font-normal" style={{ color: "#475569" }}>via Adzuna</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.listings.map((j, i) => {
                    const preview = (j.description || "").slice(0, 280);
                    const truncated = (j.description || "").length > 280;
                    const canTailor = !!(j.description && j.description.length > 50);
                    return (
                      <div
                        key={j.id || i}
                        className="rm-card-static p-5 flex flex-col"
                        style={{ background: "white" }}
                        data-testid={`job-listing-${i}`}
                      >
                        <div className="font-display text-lg uppercase leading-tight">{j.title}</div>
                        <div className="flex flex-wrap gap-3 mt-2 font-mono-rm text-xs" style={{ color: "#475569" }}>
                          <span className="flex items-center gap-1"><Building2 size={12} /> {j.company || "—"}</span>
                          {j.location && <span className="flex items-center gap-1"><MapPin size={12} /> {j.location}</span>}
                          {(j.salary_min || j.salary_max) && (
                            <span className="flex items-center gap-1">
                              <DollarSign size={12} />
                              {j.salary_min ? Math.round(j.salary_min) : "?"}{j.salary_max ? `–${Math.round(j.salary_max)}` : ""}
                            </span>
                          )}
                        </div>
                        {preview && (
                          <p className="mt-3 text-sm flex-1" style={{ color: "#475569" }}>
                            {preview}{truncated ? "…" : ""}
                          </p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2 pt-3"
                             style={{ borderTop: "1px dashed rgba(15,27,45,0.18)" }}>
                          <button
                            type="button"
                            onClick={() => tailorThisJob(j)}
                            disabled={!canTailor}
                            className="rm-btn rm-btn-primary text-xs"
                            data-testid={`tailor-this-job-${i}`}
                            title={canTailor
                              ? "Pre-fill the Tailor flow with this job description"
                              : "Description too short to tailor — open the posting first"}
                          >
                            <Wand2 size={14} /> Tailor my resume to this job →
                          </button>
                          {j.url && (
                            <a
                              href={j.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rm-btn rm-btn-ghost text-xs"
                              data-testid={`view-job-posting-${i}`}
                            >
                              <ExternalLink size={14} /> Apply on site
                            </a>
                          )}
                        </div>
                        {canTailor && (
                          <div className="mt-2 font-mono-rm text-[10.5px]" style={{ color: "#64748B" }}
                               data-testid={`apply-flow-hint-${i}`}>
                            Tip: tailor first — your result page gets a one-click
                            &ldquo;Apply on employer site&rdquo; button that tracks the application for you.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <section data-testid="job-search-links">
              <h2 className="font-display text-2xl uppercase mb-4">
                Curated search links
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rm-card p-4 flex items-center justify-between gap-3"
                    data-testid={`job-link-${i}`}
                  >
                    <span className="font-bold text-sm">{l.label}</span>
                    <ExternalLink size={16} />
                  </a>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
